"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { createAvatar } from "@dicebear/core";
import { initials } from "@dicebear/collection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAppDispatch } from "@/store/hooks";
import { clearError } from "@/store/slices/auth.slice";
import { useAuth } from "@/hooks/useAuth";
import { resetStoreSpecificState } from "@/store/actions/storeActions";
import type { AccessRole } from "@/types/auth";

export function StoreSwitcherPopover({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const {
    stores: authStores,
    currentStore,
    isLoading,
    error,
    switchStore,
  } = useAuth();
  const stores = Array.isArray(authStores) ? authStores : [];

  const [attemptingStoreId, setAttemptingStoreId] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSelect = (store: AccessRole) => {
    if (isLoading && attemptingStoreId) {
      return;
    }
    setErrorMessage(null);
    dispatch(clearError());
    dispatch(resetStoreSpecificState());
    setAttemptingStoreId(store.storeId);
    switchStore(store);
  };

  useEffect(() => {
    if (!attemptingStoreId) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (error) {
      setErrorMessage(`Failed to access store: ${error}`);
    }

    setAttemptingStoreId(null);
  }, [attemptingStoreId, isLoading, error]);

  return (
    <>
      {/* Full screen loader while switching stores - rendered at document root via portal */}
      {isLoading &&
        attemptingStoreId &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-background rounded-lg p-8 flex flex-col items-center gap-4 shadow-lg">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="text-foreground font-medium">Switching store...</p>
            </div>
          </div>,
          document.body,
        )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[340px] p-2"
          align="start"
          sideOffset={8}
        >
          <div className="px-2 pb-2 mb-2 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Switch Store
            </p>
          </div>
          {errorMessage && (
            <div className="p-2 mb-2 mx-2 bg-red-500/10 border border-red-500/20 rounded-md">
              <p className="text-red-400 text-xs">{errorMessage}</p>
            </div>
          )}
          {stores.length > 0 ? (
            stores.map((store) => {
              const seed = store.storeId || store.storeName || "store";
              const avatar = createAvatar(initials, { seed }).toDataUri();
              const isActive = currentStore?.storeId === store.storeId;
              const isCurrentlyLoading =
                isLoading && attemptingStoreId === store.storeId;

              return (
                <DropdownMenuItem
                  key={store.storeId}
                  onSelect={() => handleSelect(store)}
                  disabled={isCurrentlyLoading}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-150 ${
                    isCurrentlyLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-accent hover:scale-[1.02] focus:bg-accent focus:scale-[1.02]"
                  } ${isActive ? "bg-accent/50" : ""}`}
                >
                  <div className="h-10 w-10 min-w-[40px] flex-none rounded-full overflow-hidden ring-2 ring-border">
                    {store.logo ? (
                      <img
                        src={store.logo}
                        alt={store.storeName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <img
                        src={avatar}
                        alt={store.storeName}
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-sm">
                      {store.storeName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                      {store.domain ? (
                        <>
                          <span className="text-green-500">●</span>
                          {store.domain}
                        </>
                      ) : (
                        store.storeType
                      )}
                    </div>
                  </div>
                  <div className="text-xs">
                    {isCurrentlyLoading ? (
                      <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    ) : isActive ? (
                      <span className="text-green-500 font-medium bg-green-500/10 px-2 py-1 rounded-full">
                        Current
                      </span>
                    ) : (
                      <span className="text-muted-foreground hover:text-foreground transition-colors">
                        Switch →
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
              );
            })
          ) : (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No stores available
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground text-center px-2">
              {stores.length} store{stores.length !== 1 ? "s" : ""} available
            </p>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export default StoreSwitcherPopover;
